import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link, NavLink, Outlet, useLocation } from 'react-router-dom';

// External destinations. Update these in one place.
// JAMSERA_WEB_URL is env-overridable so `npm start` can point at a locally
// running Jamsera (see .env.development); it defaults to production.
const JAMSERA_WEB_URL =
  process.env.REACT_APP_JAMSERA_URL || 'https://jamsera.com';
// Ponger — our table-tennis app, live at its own domain.
const PONGER_WEB_URL = 'https://pongertt.com';
// Luminaq — our trivia & knowledge app, live on its own subdomain.
const LUMINAQ_WEB_URL = 'https://luminaq.jamstek.com';
// The Jamsera iOS App Store listing. The numeric id is still a placeholder
// (matches the Jamsera app's appStoreId) — swap it once the app is live.
const IOS_APP_URL = 'https://apps.apple.com/app/jamsera/id0000000000';
const CONTACT_EMAIL = 'contact@jamstek.com';

const services = [
  {
    title: 'Custom software',
    text: 'Python automation, APIs, data tooling, and internal platforms built with production discipline — not throwaway scripts.',
    points: [
      'REST and internal APIs your other systems can build on',
      'Scheduled jobs and pipelines that run unattended',
      'Admin tools and internal platforms tailored to your team',
    ],
  },
  {
    title: 'Web & mobile apps',
    text: 'React web apps and native iOS products, from marketing sites to full applications like our own Jamsera.',
    points: [
      'React front ends with real-time, shared backends',
      'Native iOS apps built and shipped to the App Store',
      'Responsive marketing sites that load fast and rank',
    ],
  },
  {
    title: 'B2B tech solutions',
    text: 'System integrations, vendor and API connections, data migrations, and IT project execution for growing businesses.',
    points: [
      'Connect CRMs, billing, and vendor APIs into one flow',
      'Data migrations with validation and zero-loss cutovers',
      'IT project execution when you need it done, not staffed',
    ],
  },
  {
    title: 'Process optimization',
    text: 'Replace fragile spreadsheets and manual handoffs with reliable tools your team can actually trust day to day.',
    points: [
      'Turn manual spreadsheet work into automated workflows',
      'Dashboards that put real numbers in front of decisions',
      'Alerts and reports so nothing slips through the cracks',
    ],
  },
];

const techStack = [
  { label: 'Python', detail: 'Automation, APIs & data' },
  { label: 'React', detail: 'Web front ends' },
  { label: 'Swift / iOS', detail: 'Native mobile' },
  { label: 'PostgreSQL', detail: 'Data & storage' },
  { label: 'REST APIs', detail: 'System integration' },
  { label: 'Cloud', detail: 'Hosting & deployment' },
];

const engagements = [
  {
    title: 'Fixed project',
    text: 'A well-defined piece of work with a clear scope, timeline, and price. Best when you know exactly what you need built.',
  },
  {
    title: 'Retainer',
    text: 'Ongoing development and support for a set amount each month — for teams that need a reliable technical partner on call.',
  },
  {
    title: 'Fractional partner',
    text: 'We act as your part-time technical team: scoping, building, and steering the roadmap without the cost of a full-time hire.',
  },
];

const approach = [
  {
    step: '01',
    title: 'Consult',
    text: 'We start with a conversation about the problem, the constraints, and what success looks like — no jargon, no upsell.',
  },
  {
    step: '02',
    title: 'Design & build',
    text: 'A clear plan, then software shipped in focused increments so you see progress and steer as we go.',
  },
  {
    step: '03',
    title: 'Support',
    text: 'We stay on after launch to maintain, extend, and hand off cleanly — the system keeps working when we step back.',
  },
];

const capabilities = [
  { label: 'Integrations', detail: 'Connect the tools you already run' },
  { label: 'Automations', detail: 'Reclaim hours of manual work' },
  { label: 'Dashboards', detail: 'Decisions backed by real data' },
];

const values = [
  {
    title: 'Production discipline',
    text: 'We write software meant to run for years, not demos meant to impress once. Tests, monitoring, and clean handoffs come standard.',
  },
  {
    title: 'Plain language',
    text: 'No jargon walls or upsell theatre. We explain trade-offs in terms of your business so you can make the call with confidence.',
  },
  {
    title: 'Ship in increments',
    text: 'You see working software early and often. Small, steerable releases beat a big-bang launch that misses the mark.',
  },
  {
    title: 'Own the outcome',
    text: 'We measure success by whether your team actually relies on what we built — not by lines of code or hours billed.',
  },
];

const stats = [
  { figure: 'Full-stack', label: 'Python, React, and native iOS under one roof' },
  { figure: 'End to end', label: 'From first consult through launch and support' },
  { figure: 'Own products', label: 'Jamsera, Ponger & Luminaq — shipped and maintained in-house' },
];

const faqs = [
  {
    q: 'What kinds of projects do you take on?',
    a: 'Custom software, integrations and automation, internal tools and dashboards, data migrations, and web or iOS applications. If it involves connecting systems or replacing fragile manual work with something reliable, it is probably a fit.',
  },
  {
    q: 'How do engagements usually start?',
    a: 'With a consultation. We talk through the problem, the constraints, and what success looks like — no obligation. From there we scope a clear plan before any code is written.',
  },
  {
    q: 'How do you price work?',
    a: 'It depends on scope. Smaller, well-defined pieces of work are quoted as fixed projects; ongoing or open-ended work is billed as a retainer or fractional engagement. We agree on the model up front so there are no surprises.',
  },
  {
    q: 'Can you work with the tools and systems we already use?',
    a: 'Yes — that is a core part of what we do. We connect to the APIs, databases, and vendors you already run rather than forcing a rip-and-replace. The goal is to fit your workflow, not the other way around.',
  },
  {
    q: 'What happens after a project launches?',
    a: 'We stay on to maintain and extend the system, and we hand off cleanly with documentation your own team can follow. You are never locked in — the software keeps working when we step back.',
  },
  {
    q: 'Do you only build things like Jamsera?',
    a: 'Jamsera is our own product and a good example of how we work, but most of what we do is client software. The same care we put into Jamsera is what we bring to your project.',
  },
];

// The JamsTek phoenix emblem, served from public/. It's a self-contained
// circular badge, so it fills its mark container rather than sitting on a
// coloured tile like the old node glyph did.
const PHOENIX_LOGO = `${process.env.PUBLIC_URL}/favicon.svg`;

function JamsTekMark() {
  return <img className="phoenix-mark" src={PHOENIX_LOGO} alt="" aria-hidden="true" />;
}

// Bounce an in-app route straight out to an external URL. Used to keep the old
// /products/* paths working now that Jamsera lives at its own domain.
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

// Jump back to the top whenever the route changes — otherwise React Router
// preserves the scroll position from the previous page.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Collapse the mobile menu whenever the route changes, so tapping a link
  // both navigates and closes the drawer.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="site-shell">
      <ScrollToTop />
      <header className="topbar">
        <Link className="brand" to="/" aria-label="JamsTek home">
          <span className="brand-mark">
            <JamsTekMark />
          </span>
          <span className="brand-name">
            JamsTek
            <em>Software &amp; consulting</em>
          </span>
        </Link>
        <nav className="topbar-nav" aria-label="Primary navigation">
          <div className="nav-item has-menu">
            <button type="button" className="nav-trigger" aria-haspopup="true" aria-expanded="false">
              Products
              <svg className="nav-caret" viewBox="0 0 12 8" width="10" height="7" aria-hidden="true" focusable="false">
                <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="nav-menu" role="menu">
              <a href={JAMSERA_WEB_URL} role="menuitem">Jamsera</a>
              <a href={PONGER_WEB_URL} role="menuitem">Ponger</a>
              <a href={LUMINAQ_WEB_URL} role="menuitem">Luminaq</a>
            </div>
          </div>
          <NavLink to="/services">Services</NavLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/contact">Contact</NavLink>
        </nav>
        <Link className="button primary compact topbar-cta" to="/contact">
          Book a consultation
        </Link>
        <button
          type="button"
          className={`nav-toggle${menuOpen ? ' open' : ''}`}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="nav-toggle-bar" />
          <span className="nav-toggle-bar" />
          <span className="nav-toggle-bar" />
        </button>
      </header>

      {/* Mobile navigation drawer — the desktop nav is hidden below 820px, so
          this hamburger-driven panel is the only way to navigate on phones. */}
      <div id="mobile-menu" className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        <nav aria-label="Mobile navigation">
          <a href={JAMSERA_WEB_URL} onClick={closeMenu}>Jamsera</a>
          <a href={PONGER_WEB_URL} onClick={closeMenu}>Ponger</a>
          <a href={LUMINAQ_WEB_URL} onClick={closeMenu}>Luminaq</a>
          <NavLink to="/services" onClick={closeMenu}>Services</NavLink>
          <NavLink to="/about" onClick={closeMenu}>About</NavLink>
          <NavLink to="/contact" onClick={closeMenu}>Contact</NavLink>
          <Link className="button primary block" to="/contact" onClick={closeMenu}>
            Book a consultation
          </Link>
        </nav>
      </div>

      <main id="top">
        <Outlet />
      </main>

      <footer>
        <div className="footer-brand">
          <span className="brand-mark small">
            <JamsTekMark />
          </span>
          <span>JamsTek · Software &amp; consulting</span>
        </div>
        <nav aria-label="Footer navigation">
          <Link to="/services">Services</Link>
          <Link to="/about">About</Link>
          <Link to="/contact">Contact</Link>
        </nav>
        <span className="footer-tag">Technology that moves your business forward.</span>
      </footer>
    </div>
  );
}

function Home() {
  return (
    <section className="hero-section">
      <div className="hero-copy">
        <p className="eyebrow">B2B software &amp; technology consulting</p>
        <h1>Technology that moves your business forward.</h1>
        <p className="hero-text">
          JamsTek is a software and consulting studio. We build custom software,
          integrations, and B2B tech solutions — and partner with you from the first
          consultation through to a system your team relies on.
        </p>
        <div className="hero-actions" aria-label="Get started">
          <Link className="button primary" to="/contact">
            Book a consultation
          </Link>
          <Link className="button secondary" to="/services">
            See what we do
          </Link>
        </div>
        <p className="hero-note">
          Custom software · Integrations &amp; automation · Web &amp; iOS apps · Fractional tech partner.
        </p>
      </div>

      <div className="hero-visual" aria-label="What JamsTek delivers">
        <div className="cap-card">
          <div className="cap-head">
            <span>Built for your workflow</span>
          </div>
          {capabilities.map((cap) => (
            <div className="cap-row" key={cap.label}>
              <span className="cap-ico" />
              <div>
                <strong>{cap.label}</strong>
                <em>{cap.detail}</em>
              </div>
            </div>
          ))}
          <div className="cap-chip-row">
            <span className="cap-chip">Python</span>
            <span className="cap-chip">React</span>
            <span className="cap-chip">iOS</span>
            <span className="cap-chip">APIs</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <>
      <section className="section">
        <div className="section-heading">
          <p className="eyebrow">What we do</p>
          <h2>Software and IT, done with production discipline.</h2>
          <p className="section-lead">
            From a single automation to a full platform, we take on the technical work
            growing businesses don&apos;t want to hire a whole team for. Here&apos;s where
            we spend most of our time.
          </p>
        </div>
        <div className="service-grid">
          {services.map((service) => (
            <article className="service-card" key={service.title}>
              <h3>{service.title}</h3>
              <p>{service.text}</p>
              <ul className="service-points">
                {service.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section stack-section">
        <div className="section-heading">
          <p className="eyebrow">Our toolkit</p>
          <h2>The technology we build with.</h2>
          <p className="section-lead">
            We&apos;re deliberately full-stack so one team can carry a project from data
            layer to interface — no handoffs, no finger-pointing.
          </p>
        </div>
        <div className="stack-grid">
          {techStack.map((tech) => (
            <div className="stack-card" key={tech.label}>
              <strong>{tech.label}</strong>
              <span>{tech.detail}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="approach-section">
        <div className="section-heading">
          <p className="eyebrow">Ways to work together</p>
          <h2>Engagements that fit the problem.</h2>
          <p className="section-lead">
            We agree on the model up front so there are no surprises — pick the shape that
            matches what you need.
          </p>
        </div>
        <div className="approach-grid">
          {engagements.map((option) => (
            <div className="approach-card" key={option.title}>
              <h3>{option.title}</h3>
              <p>{option.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section services-cta-section">
        <div className="section-heading">
          <p className="eyebrow">Let&apos;s talk</p>
          <h2>Have something in mind?</h2>
          <p className="section-lead">
            Tell us the problem and we&apos;ll help you scope the right solution — no
            obligation, no jargon.
          </p>
        </div>
        <div className="about-cta">
          <Link className="button primary" to="/contact">
            Book a consultation
          </Link>
          <Link className="button secondary" to="/about">
            How we work
          </Link>
        </div>
      </section>
    </>
  );
}

function About() {
  return (
    <>
      <section className="about-section">
        <div className="about-intro">
          <p className="eyebrow">Who we are</p>
          <h2>A software studio that ships and stays.</h2>
          <p className="section-lead about-lead">
            JamsTek is a software and consulting studio for growing businesses. We take
            on the technical work you don&apos;t want to hire a whole team for — custom
            software, integrations, automation, and the web and iOS apps that tie it all
            together. We&apos;re small on purpose, so the person who scopes your project
            is the person who builds it.
          </p>
        </div>
        <div className="stat-strip" aria-label="How we work at a glance">
          {stats.map((stat) => (
            <div className="stat-card" key={stat.figure}>
              <strong>{stat.figure}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section values-section">
        <div className="section-heading">
          <p className="eyebrow">What we value</p>
          <h2>The principles behind every build.</h2>
          <p className="section-lead">
            These aren&apos;t slogans — they&apos;re how we decide what to build, how to
            build it, and when a project is actually done.
          </p>
        </div>
        <div className="values-grid">
          {values.map((value) => (
            <article className="value-card" key={value.title}>
              <span className="value-mark">
                <JamsTekMark />
              </span>
              <h3>{value.title}</h3>
              <p>{value.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="approach-section">
        <div className="section-heading">
          <p className="eyebrow">How we work</p>
          <h2>A straightforward path from problem to product.</h2>
        </div>
        <div className="approach-grid">
          {approach.map((phase) => (
            <div className="approach-card" key={phase.step}>
              <span className="approach-step">{phase.step}</span>
              <h3>{phase.title}</h3>
              <p>{phase.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section faq-section">
        <div className="section-heading">
          <p className="eyebrow">Questions &amp; answers</p>
          <h2>Frequently asked questions.</h2>
          <p className="section-lead">
            The things clients usually want to know before we start. Don&apos;t see yours?{' '}
            <Link className="inline-link" to="/contact">
              Ask us directly
            </Link>
            .
          </p>
        </div>
        <div className="faq-list">
          {faqs.map((item) => (
            <details className="faq-item" key={item.q}>
              <summary>
                <span>{item.q}</span>
                <svg className="faq-caret" viewBox="0 0 12 8" width="12" height="8" aria-hidden="true" focusable="false">
                  <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
        <div className="about-cta">
          <Link className="button primary" to="/contact">
            Book a consultation
          </Link>
          <Link className="button secondary" to="/services">
            See what we do
          </Link>
        </div>
      </section>
    </>
  );
}

function Contact() {
  return (
    <section className="contact-section">
      <div>
        <p className="eyebrow">Book a consultation</p>
        <h2>Have a software, IT, or process problem worth solving?</h2>
        <p>
          Tell us what you&apos;re trying to do. We&apos;ll get back to you to set up a
          consultation — no obligation, no jargon. We read every note.
        </p>
        <a className="contact-email" href={`mailto:${CONTACT_EMAIL}`}>
          {CONTACT_EMAIL}
        </a>
      </div>
      <form
        className="contact-form"
        action={`mailto:${CONTACT_EMAIL}`}
        method="post"
        encType="text/plain"
      >
        <label>
          Name
          <input type="text" name="name" autoComplete="name" />
        </label>
        <label>
          Email
          <input type="email" name="email" autoComplete="email" />
        </label>
        <label>
          Company <span className="optional">(optional)</span>
          <input type="text" name="company" autoComplete="organization" />
        </label>
        <label>
          How can we help?
          <textarea name="message" rows="5" />
        </label>
        <button type="submit">Send message</button>
      </form>
    </section>
  );
}

function NotFound() {
  return (
    <section className="section notfound-section">
      <div className="section-heading">
        <p className="eyebrow">404</p>
        <h2>We couldn&apos;t find that page.</h2>
        <p className="section-lead">
          The link may be old or mistyped. Head back to the homepage to find your way.
        </p>
      </div>
      <div className="notfound-actions">
        <Link className="button primary" to="/">
          Back to home
        </Link>
      </div>
    </section>
  );
}

function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="services" element={<Services />} />
        <Route path="about" element={<About />} />
        {/* How-we-work and FAQ now live on the About page; keep old links working. */}
        <Route path="how-we-work" element={<Navigate to="/about" replace />} />
        <Route path="faq" element={<Navigate to="/about" replace />} />
        {/* Jamsera now lives at its own domain; send product links straight there.
            The old /products/jamseras and /products/knowwhere paths are kept so
            pre-rename links still work. */}
        <Route
          path="products/jamsera"
          element={<ExternalRedirect to={JAMSERA_WEB_URL} />}
        />
        <Route
          path="products/jamseras"
          element={<ExternalRedirect to={JAMSERA_WEB_URL} />}
        />
        <Route
          path="products/knowwhere"
          element={<ExternalRedirect to={JAMSERA_WEB_URL} />}
        />
        <Route path="contact" element={<Contact />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default App;
